import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import { ops } from '../api/operations';
import type { StreamState } from '../api/stream';
import type { Job } from '../api/types';
import { Button } from './Button';
import { Badge, type Tone } from './Chip';

const DONE = new Set(['succeeded', 'failed', 'cancelled']);

/** The tone of a job status everywhere (spec/dashboard.md section 12.2): queued / running info, succeeded success, failed danger, cancelled warning. */
export const JOB_TONE: Record<string, Tone> = { queued: 'info', running: 'info', succeeded: 'success', failed: 'danger', cancelled: 'warning' };

/** Where the job lives: with it, JobProgress follows the job's event stream, polls `GET …/jobs/{job}` and offers Cancel / Retry. */
export interface JobApi {
  client: ApiClient;
  org: string;
  game: string;
  /** the permission Cancel / Retry need when the caller lacks it (they are disabled with it in the title) */
  missingPermission?: string | null;
  /** the stream client's wait between reconnects (tests) */
  streamSleep?: (ms: number, signal: AbortSignal) => Promise<void>;
}

interface JobProgressProps {
  job: Job;
  /** the poll's read; with `api` it defaults to `GET …/jobs/{job}` of the job on screen */
  load?: () => Promise<Job>;
  onDone?: (job: Job) => void;
  /** a Retry queued this new job: the component follows it from now on */
  onRetry?: (job: Job) => void;
  intervalsMs?: number[];
  api?: JobApi;
  /** false hides Cancel and Retry (they need `api`) */
  controls?: boolean;
}

/**
 * Renders a job and follows it until it finishes (spec section 2.2): with `api`, through `GET …/jobs/{job}/events`
 * (`job.progress` moves the bar and the message, `job.finish` ends it and the JobDto is read once for the result), with the
 * poll — a growing interval — as the fallback while the stream is not connected; without `api`, `load` is polled. `onDone`
 * fires once per finished job, also for a job that is already finished when it mounts; the callbacks are read through refs,
 * so an inline closure never restarts the follow. With `api`, Cancel (`POST …:cancel`) shows while the job is queued or
 * running and Retry (`POST …:retry`) on a failed or cancelled one, after which the new job is followed.
 */
export function JobProgress({ job: initial, load, onDone, onRetry, intervalsMs = [1000, 2000, 3000, 5000], api, controls = true }: JobProgressProps) {
  const [job, setJob] = useState(initial);
  const [streamState, setStreamState] = useState<StreamState | null>(null);
  const [busy, setBusy] = useState<'cancel' | 'retry' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const apiRef = useRef(api);
  apiRef.current = api;
  const readJob = (id: string): (() => Promise<Job>) | undefined => {
    const a = apiRef.current;
    if (a) return () => a.client.data(ops.getJob, { path: { org: a.org, game: a.game, job: id } });
    return loadRef.current;
  };
  const loadRef = useRef(load);
  const onDoneRef = useRef(onDone);
  const onRetryRef = useRef(onRetry);
  loadRef.current = load;
  onDoneRef.current = onDone;
  onRetryRef.current = onRetry;
  const reported = useRef<string | null>(null);
  useEffect(() => setJob(initial), [initial]);
  useEffect(() => {
    if (!DONE.has(job.status)) return;
    const key = `${job.id}:${job.status}`;
    if (reported.current === key) return;
    reported.current = key;
    onDoneRef.current?.(job);
  }, [job]);

  const done = DONE.has(job.status);
  const hasApi = !!api;

  // the stream: job.progress and job.finish, from the job's start (it ends after job.finish)
  useEffect(() => {
    const a = apiRef.current;
    if (!a || done) return;
    const abort = new AbortController();
    const id = job.id;
    void a.client.stream(ops.streamJobEvents, {
      path: { org: a.org, game: a.game, job: id },
      signal: abort.signal,
      sleep: a.streamSleep,
      onStateChange: (state) => {
        if (!abort.signal.aborted) setStreamState(state);
      },
      onFrame: (frame) => {
        if (abort.signal.aborted) return;
        const payload = ((frame.data as { data?: unknown } | null)?.data ?? {}) as { progress?: number | string; message?: string | null; status?: string; cancelRequestedAt?: string | null };
        if (frame.event === 'job.cancel' && payload.status !== 'cancelled') {
          // a running job was asked to stop: it ends at its next progress report (job.finish)
          setJob((j) => (j.id !== id || DONE.has(j.status) ? j : { ...j, cancelRequestedAt: payload.cancelRequestedAt ?? j.cancelRequestedAt ?? new Date().toISOString() }));
        } else if (frame.event === 'job.progress') {
          setJob((j) => (j.id !== id || DONE.has(j.status) ? j : { ...j, status: 'running', progress: payload.progress !== undefined ? (Number(payload.progress) as never) : j.progress, progressMessage: payload.message ?? j.progressMessage }));
        } else if (frame.event === 'job.finish' || frame.event === 'job.cancel') {
          // the final state and the result: one read of the JobDto
          abort.abort();
          setStreamState('stopped');
          const read = a.client.data(ops.getJob, { path: { org: a.org, game: a.game, job: id } });
          read.then(
            (next) => setJob((j) => (j.id === id ? next : j)),
            () => setStreamState('reconnecting'), // the poll below takes over
          );
        }
      },
    });
    return () => abort.abort();
  }, [job.id, done, hasApi]); // eslint-disable-line react-hooks/exhaustive-deps

  // the poll: always without `api`; with it, only while the stream is not connected
  const streaming = hasApi && (streamState === 'connected' || streamState === 'connecting' || streamState === null);
  useEffect(() => {
    if (done || streaming) return;
    const read = readJob(job.id);
    if (!read) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const r = readJob(job.id);
      if (!r) return;
      try {
        const next = await r();
        if (cancelled) return;
        setJob(next);
        if (DONE.has(next.status)) return; // the effect above reports it once
      } catch {
        // a transport failure: keep polling
      }
      attempt++;
      timer = setTimeout(tick, intervalsMs[Math.min(attempt, intervalsMs.length - 1)]!);
    };
    timer = setTimeout(tick, intervalsMs[0]!);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [job.id, job.status, done, streaming, intervalsMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    const a = apiRef.current;
    if (!a) return;
    setBusy('cancel');
    setActionError(null);
    try {
      const next = await a.client.data(ops.cancelJob, { path: { org: a.org, game: a.game, job: job.id } });
      setJob((j) => (j.id === next.id ? next : j));
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const retry = async () => {
    const a = apiRef.current;
    if (!a) return;
    setBusy('retry');
    setActionError(null);
    try {
      const next = await a.client.data(ops.retryJob, { path: { org: a.org, game: a.game, job: job.id } });
      setStreamState(null);
      setJob(next);
      onRetryRef.current?.(next);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const error = job.error as { code?: string; message?: string } | null | undefined;
  const missing = api?.missingPermission ?? null;
  const cancelRequested = !!(job as { cancelRequestedAt?: string | null }).cancelRequestedAt;
  return (
    <div className={`job job-${job.status}`} role="status" aria-live="polite">
      <div className="row">
        <code>{job.kind}</code> <Badge tone={JOB_TONE[job.status] ?? 'neutral'}>{job.status}</Badge>
        {hasApi && controls && !done && (
          <Button size="s" onClick={() => void cancel()} busy={busy === 'cancel'} disabled={cancelRequested} missingPermission={missing} title={cancelRequested ? 'Cancel requested: the worker stops at its next progress report.' : undefined}>
            {cancelRequested ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
        {hasApi && controls && (job.status === 'failed' || job.status === 'cancelled') && (
          <Button size="s" onClick={() => void retry()} busy={busy === 'retry'} missingPermission={missing}>
            Retry
          </Button>
        )}
      </div>
      <progress value={job.progress} max={100} aria-label={`${job.kind} progress`}>
        {job.progress}%
      </progress>
      <div className="muted">{job.progressMessage ?? (done ? 'finished' : 'waiting for the worker…')}</div>
      {error && (
        <div className="error">
          {error.code}: {error.message}
        </div>
      )}
      {actionError && (
        <div className="error" role="alert">
          {actionError}
        </div>
      )}
    </div>
  );
}
